'use client';

import {
  ExperimentProvider as CarvanaExperimentProvider,
  ExperimentProviderContext as CarvanaExperimentProviderContext
} from '@carvana/experiment';
import React from 'react';
import { settings } from '~/constants/common/experiments';
import { Experiments } from '~/contracts/common';
import useExperimentsWithIdentifiers from '~/hooks/common/experiments/useExperimentsWithIdentifiers';
import BrowserCookies from '~/utilities/BrowserCookies';

export type PropsFromServer = {
  browserCookieId: string;
  experiments: Partial<Experiments>;
  serverCookies: Record<string, string>;
  userId: string;
};

type OwnProps = React.PropsWithChildren<
  PropsFromServer & {
    browserCookieIdExperiments: Partial<Experiments>;
    userIdExperiments: Partial<Experiments>;
  }
>;

export default function ExperimentProvider(props: OwnProps) {
  const experimentsWithIdentifiers = useExperimentsWithIdentifiers({
    browserCookieId:
      props.browserCookieId || BrowserCookies.get('BrowserCookieId') || '',
    browserCookieIdExperiments: props.browserCookieIdExperiments,
    userId: props.userId,
    userIdExperiments: props.userIdExperiments
  });

  return (
    <CarvanaExperimentProvider
      defaults={settings}
      onPageLoadExperiments={experimentsWithIdentifiers}
      preloadedExperiments={props.experiments}
      serverCookies={props.serverCookies}
    >
      {props.children}
    </CarvanaExperimentProvider>
  );
}

type MockOwnProps = React.PropsWithChildren<{
  experiments: Record<string, 'control' | 'treatment'>;
}>;

export function MockExperimentContextProvider({
  experiments,
  children
}: MockOwnProps) {
  const experimentsData = Object.fromEntries(
    Object.keys(experiments).map(expName => [
      expName,
      {
        assignedBucket: experiments[expName],
        buckets: ['control', 'treatment'],
        defaultBucket: 'control',
        experimentName: expName,
        hasErrored: false,
        isLoading: false
      }
    ])
  );

  return (
    <CarvanaExperimentProviderContext.Provider
      value={{
        defaults: {
          cacheMinutes: 30,
          cookieRefreshWindowMinutes: 15,
          environment: 'test'
        },
        experiments: experimentsData,
        serverCookies: {},
        updateExperiment: jest.fn()
      }}
    >
      {children}
    </CarvanaExperimentProviderContext.Provider>
  );
}
